// <copyright file="RoadSideUnitGeoJson.tsx">
// © 2023 Florida Department of Transportation
//
// The copyright to the computer program(s) and source code herein is
// the property of the Florida Department of Transportation.
// The program(s) and source code may be used and/or copied only with the
// written permission of the Florida Department of Transportation or
// in accordance with the terms and conditions stipulated in the
// agreement/contract under which the program(s) and source code have
// been supplied.
// </copyright>

import { default as LEAF } from "leaflet";
import React from "react";
import { GeoJSON } from "react-leaflet";
import rsuImg from "src/components/VIPER/images/RSU_Icon.png";
import { popupInfoTable } from "src/components/VIPER/utils/MapUtils";
import type { FeatureCollection } from "@turf/helpers";
import type { Feature } from "geojson";
import type { LatLngExpression, Layer } from "leaflet";
import type { FC } from "react";
import type { PopupInfoTableProps } from "src/components/VIPER/utils/MapUtils";

// Properties to initialize this UI component
interface RoadSideUnitGeoJsonProps {
    rsuData: FeatureCollection; // rsu data retrieved from back end, state lifted to parent
    colorMap: Map<string, string>; // color per rsu
    selectedRSU: string[]; // device filter selections
    rsuUpdateKey: string; // unique key
}

// API data model for RSU agency list - MUST match API response
export interface RsuAgencies {
    rsus: RsuAgency[];
}

// API data model for RSU agency info - MUST match API response
export interface RsuAgency {
    agencyName: string;
    sourceDeviceIds: string[];
}

/**
 * Leaflet function called on each feature before adding it to a GeoJSON layer
 * @param feature Feature to be added to layer
 * @param layer Layer onto which feature will be added
 */
function onEachFeature(feature: Feature, layer: Layer) {
    if (feature.properties) {
        const tableProps: PopupInfoTableProps = {
            title: "RSU Information:",
            data: Object.entries(feature.properties),
        };
        layer.bindPopup(popupInfoTable(tableProps));
    }
}

/**
 * Create Leaflet marker icon for RSU map feature with color based on BSM count
 * @param opacity Visibility of marker
 * @param color Color of marker
 * @returns Marker icon for an RSU map feature
 */
function RsuIconBuilder(opacity: number, color: string) {
    let fillColor = "orange";
    if (color != null) {
        fillColor = color;
    }
    const spanMarker = `
        background-color: ${fillColor};
        opacity: ${opacity};
        width: 100%;
        height: 100%;
        display: block;
        // left: -50%;
        // top: -50%;
        position: relative;
        border-radius: 50% 50% 0;
        transform: rotate(45deg);
        border: 1px solid #FFFFFF;`;

    return LEAF.divIcon({
        iconAnchor: [20, 40],
        popupAnchor: [0, -40],
        iconSize: [40, 40],
        className: "",
        html: `<div style="width:100%;height:100%;background: transparent;display: flex;justify-content: bottom;align-items: left;">
                <span style="${spanMarker}">
                <div style="transform: rotate(-45deg);display: flex;justify-content: center;align-items: center;margin-right: 10%;">
                <img src="${rsuImg}" alt="icon-rsu" style="width:70%;height:70%;background: transparent;padding-top: 10%;"/>
                </div>
                </span>
                </div>`,
    });
}

const RoadSideUnitGeoJson: FC<RoadSideUnitGeoJsonProps> = (
    props: RoadSideUnitGeoJsonProps
) => {
    const geoJsonLayer = React.createRef<LEAF.GeoJSON>();

    /**
     * Leaflet function to override default display of points on GeoJSON layer
     * @param feature Point feature to display
     * @param latlng Point feature location
     */
    function pointToLayer(feature: Feature, latlng: LatLngExpression) {
        const color =
            (feature?.properties?.sourceDeviceId &&
                props.colorMap.get(feature.properties.sourceDeviceId)) ||
            null;
        return LEAF.marker(latlng, { icon: RsuIconBuilder(1.0, color) });
    }

    /**
     * Callback to decide whether to filter or not
     * @param {GeoJSON.Feature} feature The feature to decide whether to filter
     * @returns Whether to filter or not
     */
    const filterFunction = (feature: GeoJSON.Feature): boolean => {
        try {
            if (feature.properties) {
                return props.selectedRSU.includes(
                    feature?.properties?.sourceDeviceId
                );
            } else {
                return false;
            }
        } catch (error) {
            return true;
        }
    };

    return (
        <GeoJSON
            key={props.rsuUpdateKey}
            ref={geoJsonLayer}
            data={props.rsuData}
            onEachFeature={onEachFeature}
            pointToLayer={pointToLayer}
            filter={filterFunction}
        />
    );
};

export default RoadSideUnitGeoJson;
